"use client";

import { useEffect, useState, useRef } from "react";
import { parseCookies, setCookie } from "nookies";
import { Globe, ChevronDown, Check } from "lucide-react";

const COOKIE_NAME = "googtrans";

interface LanguageDescriptor {
  name: string;
  title: string;
}

declare global {
  namespace globalThis {
    var __GOOGLE_TRANSLATION_CONFIG__: {
      languages: LanguageDescriptor[];
      defaultLanguage: string;
    };
  }
}

const LanguageSwitcher = () => {
  const [currentLanguage, setCurrentLanguage] = useState<string>();
  const [languageConfig, setLanguageConfig] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cookies = parseCookies();
    const existingLanguageCookieValue = cookies[COOKIE_NAME];
    let languageValue;
    if (existingLanguageCookieValue) {
      const sp = existingLanguageCookieValue.split("/");
      if (sp.length > 2) languageValue = sp[2];
    }
    if (global.__GOOGLE_TRANSLATION_CONFIG__ && !languageValue) {
      languageValue = global.__GOOGLE_TRANSLATION_CONFIG__.defaultLanguage;
    }
    if (languageValue) setCurrentLanguage(languageValue);
    if (global.__GOOGLE_TRANSLATION_CONFIG__) setLanguageConfig(global.__GOOGLE_TRANSLATION_CONFIG__);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!currentLanguage || !languageConfig) return null;

  const switchLanguage = (lang: string) => {
    setCookie(null, COOKIE_NAME, "/auto/" + lang);
    window.location.reload();
  };

  const currentLanguageTitle =
    languageConfig.languages.find((ld: LanguageDescriptor) => ld.name === currentLanguage)?.title ?? "English";

  const filteredLanguages = languageConfig.languages.filter((ld: LanguageDescriptor) =>
    ld.title.toLowerCase().includes(search.toLowerCase())
  );

  // Abbreviation for the trigger button (e.g. "EN", "HI")
  const langAbbr = currentLanguage.toUpperCase().slice(0, 2);

  return (
    <div className="relative flex-shrink-0 notranslate" ref={dropdownRef} style={{ fontFamily: "'Montserrat', sans-serif" }}>

      {/* Trigger */}
      <button
        onClick={() => { setIsOpen((prev) => !prev); setSearch(""); }}
        className={[
          "flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold select-none outline-none transition-all duration-200 border",
          isOpen
            ? "bg-[#f5ede4] border-[#c9a080] text-[#6b4226] shadow-[0_2px_12px_rgba(168,112,78,0.18)]"
            : "bg-white border-stone-200 text-stone-700 hover:border-[#c9a080] hover:bg-[#fdf8f4] hover:text-[#6b4226] shadow-sm hover:shadow-[0_2px_12px_rgba(168,112,78,0.12)]"
        ].join(" ")}
      >
        <span
          className="flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-bold leading-none flex-shrink-0"
          style={{
            background: isOpen ? '#a8704e' : '#e8d5c4',
            color: isOpen ? '#fff' : '#7a4f35',
          }}
        >
          {langAbbr}
        </span>
        <span className="uppercase tracking-widest text-[10px]">{currentLanguageTitle}</span>
        <ChevronDown
          className={`w-3 h-3 opacity-50 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className="absolute top-[calc(100%+8px)] right-0 min-w-[180px] bg-white rounded-2xl z-[999] overflow-hidden"
          style={{
            boxShadow: '0 20px 60px rgba(100,60,30,0.16), 0 4px 16px rgba(100,60,30,0.08)',
            border: '1px solid rgba(210,185,165,0.4)',
            animation: 'cd-appear 0.22s cubic-bezier(0.16, 1, 0.3, 1) both',
            transformOrigin: 'top right',
          }}
        >
          {/* Header */}
          <div
            className="px-4 pt-3.5 pb-2.5"
            style={{
              background: 'linear-gradient(135deg, #fdf7f2 0%, #f5ede4 100%)',
              borderBottom: '1px solid rgba(210,185,165,0.3)',
            }}
          >
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '11px', fontWeight: 600, fontStyle: 'italic', letterSpacing: '0.12em', color: '#a09080' }}>
              Select Language
            </p>
            {/* Search */}
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="mt-2 w-full px-2.5 py-1.5 text-[11px] rounded-lg outline-none transition-all"
              style={{
                background: '#fff',
                border: '1px solid rgba(210,185,165,0.5)',
                color: '#5a3a20',
                fontFamily: "'Montserrat', sans-serif",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#c9a080')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(210,185,165,0.5)')}
            />
          </div>

          {/* List */}
          <div className="cd-scroll py-1.5 max-h-56 overflow-y-auto">
            {filteredLanguages.length === 0 ? (
              <p className="px-4 py-3 text-[11px] text-center text-stone-400">No results found</p>
            ) : (
              filteredLanguages.map((ld: LanguageDescriptor) => {
                const isActive = currentLanguage === ld.name;
                const abbr = ld.name.toUpperCase().slice(0, 2);
                return (
                  <button
                    key={ld.name}
                    onClick={() => switchLanguage(ld.name)}
                    className={`cd-item w-full px-3.5 py-2.5 text-left flex items-center justify-between gap-2 group ${isActive ? "bg-[#fdf0e6]" : "hover:bg-[#fdf7f3]"}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="cd-symbol w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                        style={
                          isActive
                            ? { background: 'linear-gradient(135deg, #c9845a, #a8704e)', color: '#fff', boxShadow: '0 3px 10px rgba(168,112,78,0.35)' }
                            : { background: '#f0e8e0', color: '#9a6b4e' }
                        }
                      >
                        {abbr}
                      </div>
                      <p className={`text-[11px] font-bold uppercase tracking-[0.1em] truncate ${isActive ? "text-[#7a4a2a]" : "text-stone-600 group-hover:text-stone-900"}`}>
                        {ld.title}
                      </p>
                    </div>

                    {isActive ? (
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #c9845a, #a8704e)', boxShadow: '0 2px 6px rgba(168,112,78,0.4)' }}>
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </div>
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-stone-200 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer accent line */}
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #e8c9ad, #c9845a, #e8c9ad)' }} />
        </div>
      )}
    </div>
  );
};

export { LanguageSwitcher, COOKIE_NAME };